import React, { useState, useMemo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Business as BusinessIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  ManageSearch as ManageSearchIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSidebarOpen } from '@/store/slices/uiSlice';
import { routes } from '@/router';
import {
  canAccessComplianceStatusLookup,
  canViewServiceProviders,
  hasAnyRole,
  hasInternalVisibility,
  isAdmin,
  isFieldUser,
  UserRole,
} from '@/utils/roleHelpers';
import { useComplianceReviewAccess } from '@/hooks/useComplianceReviewAccess';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

const NavItemIcon: React.FC<{ sidebarOpen: boolean; children: React.ReactNode }> = ({ sidebarOpen, children }) => (
  <ListItemIcon sx={{ minWidth: 0, mr: sidebarOpen ? 3 : 'auto', justifyContent: 'center' }}>
    {children}
  </ListItemIcon>
);

const SubMenuItem: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({
  label,
  selected,
  onClick,
}) => (
  <ListItemButton sx={{ pl: 4 }} selected={selected} onClick={onClick}>
    <ListItemText primary={label} />
  </ListItemButton>
);

const NavMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  sidebarOpen: boolean;
  onClick: () => void;
}> = ({ icon, label, selected, sidebarOpen, onClick }) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={selected}
        onClick={onClick}
        sx={{
          minHeight: 44,
          justifyContent: sidebarOpen ? 'initial' : 'center',
          px: 2.5,
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            '&:hover': { backgroundColor: theme.palette.primary.main },
          },
        }}
      >
        <NavItemIcon sidebarOpen={sidebarOpen}>{icon}</NavItemIcon>
        <ListItemText primary={label} sx={{ opacity: sidebarOpen ? 1 : 0 }} />
      </ListItemButton>
    </ListItem>
  );
};

const AppSidebar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('navigation');

  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [complianceManagementOpen, setComplianceManagementOpen] = useState(
    location.pathname.startsWith('/compliance-management')
  );
  const [complianceRequirementsOpen, setComplianceRequirementsOpen] = useState(
    location.pathname.startsWith('/compliance-requirements')
  );
  const [manageOpen, setManageOpen] = useState(
    location.pathname.startsWith('/compliance-status-lookup') ||
      location.pathname.startsWith('/manage')
  );
  const { canAccessComplianceReview } = useComplianceReviewAccess();

  const {
    userIsAdmin,
    userCanViewServiceProviders,
    userIsFieldUser,
    userIsSpUser,
    userHasInternalVisibility,
    showComplianceStatusLookup,
  } = useMemo(() => {
    const roles = user?.roles ?? [];
    const organization = user?.organizationName ?? "";
    return {
      userIsAdmin: isAdmin(roles),
      userCanViewServiceProviders: canViewServiceProviders(roles, organization),
      userIsFieldUser: isFieldUser(roles),
      // SpAdmin + SpStaff are the SP-side staff who own their own SP's users.
      // Used for menu-item visibility on SP-users pages and mirrors
      // AdminOrSpStaffRoute.
      userIsSpUser: hasAnyRole(roles, [UserRole.SpAdmin, UserRole.SpStaff]),
      userHasInternalVisibility: hasInternalVisibility(roles, organization),
      // Shared with the compliance-status-lookup/:tab route guard
      // (canAccessComplianceStatusLookup) so nav visibility and route
      // access can't drift apart again.
      showComplianceStatusLookup: canAccessComplianceStatusLookup(roles, organization),
    };
  }, [user]);

  const handleDrawerClose = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  const createToggleHandler = (setOpen: React.Dispatch<React.SetStateAction<boolean>>, isOpen: boolean) => () => {
    if (!sidebarOpen) {
      dispatch(setSidebarOpen(true));
      setOpen(true);
    } else {
      setOpen(!isOpen);
    }
  };

  const handleComplianceManagementToggle = createToggleHandler(setComplianceManagementOpen, complianceManagementOpen);
  const handleComplianceRequirementsToggle = createToggleHandler(setComplianceRequirementsOpen, complianceRequirementsOpen);
  const handleManageToggle = createToggleHandler(setManageOpen, manageOpen);

  const allMenuItems = useMemo(() => [
    {
      key: 'dashboard',
      label: t('dashboard'),
      icon: <DashboardIcon />,
      path: routes.dashboard.path,
      requiresAdmin: false,
    },
    {
      key: 'serviceProviders',
      label: t('serviceProviders'),
      icon: <BusinessIcon />,
      path: routes.serviceProviders.path,
      // Admin (full CRUD) or User (read-only) in a Service Provider-scoped
      // org — not Global Admin-only.
      requiresServiceProviderAccess: true,
    },
    {
      key: 'serviceProviderUsers',
      label: t('serviceProviderUsers'),
      icon: <GroupsOutlinedIcon />,
      path: routes.serviceProviderUsers.path,
      requiresAdmin: true, // Admin only
      allowSpUser: true, // Also allow SpAdmin / SpStaff — mirrors AdminOrSpStaffRoute
    },
    {
      key: 'internalUsers',
      label: t('internalUsers'),
      icon: <AdminPanelSettingsIcon />,
      path: routes.internalUsers.path,
      requiresAdmin: true,
    },
    {
      key: 'profile',
      label: t('profile'),
      icon: <PersonIcon />,
      path: routes.profile.path,
      requiresAdmin: false,
    },
    {
      key: 'settings',
      label: t('settings'),
      icon: <SettingsIcon />,
      path: routes.settings.path,
      requiresAdmin: false,
    },
  ], [t]);

  // Filter menu items based on user role
  const menuItems = useMemo(() => {
    return allMenuItems.filter((item: any) => {
      if (item.requiresServiceProviderAccess) {
        return userCanViewServiceProviders;
      }
      if (item.requiresAdmin) {
        // Admin-only items may opt-in via allowSpUser to also render for
        // SP-side staff (SpAdmin / SpStaff) — same audience as
        // AdminOrSpStaffRoute so nav + guard stay aligned. Admins who can
        // already reach Service Provider Users via the SP detail page
        // (canViewServiceProviders — the 5 CRUD orgs plus the SMT read-only
        // org) are excluded here so the duplicate entry point goes away for
        // them specifically, without touching SpAdmin/SpStaff or admins in
        // any other org.
        if (item.allowSpUser) {
          return userIsSpUser || (userIsAdmin && !userCanViewServiceProviders);
        }
        return userIsAdmin;
      }
      return true;
    });
  }, [allMenuItems, userIsAdmin, userCanViewServiceProviders, userIsSpUser]);

  const drawer = (
    <>
      <Toolbar /> {/* Spacer for AppBar */}
      <List>
        {menuItems.slice(0, 1).map((item) => (
          <NavMenuItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick(item.path)}
          />
        ))}

        {/* Compliance menu — Drivers, Monitors, and SP-side staff (SpStaff /
            SpAdmin) see Compliance Management; global-side admins see
            Compliance Requirements. SpAdmin used to fall through to the wrong
            branch because isFieldUser only recognized Driver/Monitor +
            env-configured provider-submitter roles (default 'SpStaff,SpAdmin',
            via VITE_PROVIDER_SUBMITTER_ROLES); widen with userIsSpUser so
            SpAdmin gets the same UX as SpStaff even if that default narrows. */}
        {(userIsFieldUser || userIsSpUser) ? (
          <>
            <NavMenuItem
              icon={<AccountBalanceIcon />}
              label={t('complianceManagement')}
              selected={location.pathname.startsWith('/compliance-management')}
              sidebarOpen={sidebarOpen}
              onClick={handleComplianceManagementToggle}
            />

            <Collapse in={complianceManagementOpen && sidebarOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <SubMenuItem
                  label={t('dashboard')}
                  selected={location.pathname === '/compliance-management'}
                  onClick={() => handleItemClick('/compliance-management')}
                />
                <SubMenuItem
                  label={t('requirementsOverview')}
                  selected={location.pathname.startsWith('/compliance-management/requirements')}
                  onClick={() => handleItemClick('/compliance-management/requirements')}
                />
              </List>
            </Collapse>
          </>
        ) : (
          <>
            <NavMenuItem
              icon={<AccountBalanceIcon />}
              label={t('complianceRequirements')}
              selected={location.pathname.startsWith('/compliance-requirements')}
              sidebarOpen={sidebarOpen}
              onClick={handleComplianceRequirementsToggle}
            />

            <Collapse in={complianceRequirementsOpen && sidebarOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <SubMenuItem
                  label={t('everdriven')}
                  selected={location.pathname === '/compliance-requirements/everdriven'}
                  onClick={() => handleItemClick('/compliance-requirements/everdriven')}
                />
                <SubMenuItem
                  label={t('states')}
                  selected={location.pathname === '/compliance-requirements/states'}
                  onClick={() => handleItemClick('/compliance-requirements/states')}
                />
                <SubMenuItem
                  label={t('districts')}
                  selected={location.pathname === '/compliance-requirements/districts'}
                  onClick={() => handleItemClick('/compliance-requirements/districts')}
                />
              </List>
            </Collapse>
          </>
        )}

        {/* Compliance Review - Administrator/User in an approving org or ProductAndEngineering */}
        {canAccessComplianceReview && (
          <NavMenuItem
            icon={<AssignmentTurnedInIcon />}
            label={t('complianceReview')}
            selected={location.pathname.startsWith('/compliance-review')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick('/compliance-review')}
          />
        )}

        {/* Compliance Status Lookup — provider submitter roles + internal SP-visibility users */}
        {showComplianceStatusLookup && (
          <>
            <NavMenuItem
              icon={<ManageSearchIcon />}
              label={t('complianceStatusLookup')}
              selected={location.pathname.startsWith('/compliance-status-lookup')}
              sidebarOpen={sidebarOpen}
              onClick={handleManageToggle}
            />

            <Collapse in={manageOpen && sidebarOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {userHasInternalVisibility && (
                  <SubMenuItem
                    label={t('serviceProvider')}
                    selected={location.pathname === '/compliance-status-lookup/service-providers'}
                    onClick={() => handleItemClick('/compliance-status-lookup/service-providers')}
                  />
                )}
                <SubMenuItem
                  label={t('driversMonitors')}
                  selected={location.pathname === '/compliance-status-lookup/drivers-monitors'}
                  onClick={() => handleItemClick('/compliance-status-lookup/drivers-monitors')}
                />
                {!userHasInternalVisibility && (
                  <SubMenuItem
                    label={t('fleetVehicles')}
                    selected={location.pathname === '/compliance-status-lookup/fleet-vehicles'}
                    onClick={() => handleItemClick('/compliance-status-lookup/fleet-vehicles')}
                  />
                )}
              </List>
            </Collapse>
          </>
        )}

        {menuItems.slice(1).map((item) => (
          <NavMenuItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick(item.path)}
          />
        ))}
      </List>
      <Divider />
    </>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={sidebarOpen}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        width: sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          boxSizing: 'border-box',
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default AppSidebar;
